<template>
  <div>
    <card-wrapper
      :title="$t('leads.detail.title')"
      :show-divider="false"
      :hide-close-btn="false"
      class="leads-detail-wrapper"
      id="leads-detail"
      @openCloseCancelDialog="goBack">
      <div class="card-spacing">
        <v-card :elevation="0" rounded="lg" class="card-outline">
          <v-row class="title-row no-margin">
            <div class="button-wrapper">
              <m-btn
                :disabled="isLost"
                @click="createOffer"
                class="no-shadow button-padding"
                color="secondary"
                v-if="!offerId && !isLoading">
                <span class="button-padding">
                  <v-icon dense color="white">fak fa-light fa-m-file-plus</v-icon>
                  <span class="ml-2">{{ $t('leads.list.actions.create-offer') }}</span>
                </span>
              </m-btn>

              <m-btn color="secondary" class="no-shadow button-padding" @click="seeOffer" v-else-if="!isLoading">
                <span class="button-padding">
                  <v-icon dense color="white">fak fa-light fa-m-file-xmark</v-icon>
                  <span class="ml-2">{{ $t('leads.list.actions.see-offer') }}</span>
                </span>
              </m-btn>

              <m-btn
                v-if="canCreateAppointment"
                variant="outlined"
                color="black"
                @click="openAppointmentForm"
                :disabled="isLost || isLoading">
                <span class="button-padding">
                  <v-icon dense>fak fa-light fa-m-calendar</v-icon>
                  <span class="ml-2">{{ $t('leads.list.actions.create-appointment') }}</span>
                </span>
              </m-btn>

              <m-btn variant="outlined" color="gray" @click="openConfirmDialog" :disabled="isLost">
                <span class="button-padding">
                  <v-icon dense>fak fa-light fa-m-file-arrow-right</v-icon>
                  <span class="ml-2">{{ $t('leads.list.actions.mark-lost') }}</span>
                </span>
              </m-btn>
            </div>
          </v-row>

          <v-row class="lead-layout-spacing no-margin">
            <v-col cols="12" lg="5" xl="4" class="lead-grid" id="lead-grid-left-column">
              <lead-detail-card
                :items="clientItems"
                :title="$t('leads.detail.title-client')"
                :loading="isLoading"
                :country="leadCountry" />
              <lead-detail-card
                :items="leadItems"
                :title="$t('leads.detail.title-lead')"
                :loading="isLoading"
                :country="leadCountry">
                <template #assignee="{data}">
                  <lead-assignee-select
                    :agent="data.agent"
                    :initial-agent="data.initialAgent"
                    :dealer-users="data.dealerUsers"
                    :is-dealer-admin="data.isDealerAdmin"
                    :is-sales-agent="data.isSalesAgent"
                    :user-email="data.userEmail"
                    :status="data.status"
                    @input="data.onAssigneeChange" />
                </template>
              </lead-detail-card>
            </v-col>

            <v-col cols="12" lg="7" xl="8" class="lead-grid pl-lead-grid">
              <v-expansion-panels v-model="panels" multiple flat class="min-height">
                <expansion-panel
                  key="expansion-panel-information-data"
                  :title="$t('leads.detail.title-info')"
                  class="expansion-panel-padding"
                  :hide-content-margins="true">
                  <template v-slot:panel-content class="p-0">
                    <lead-detail-card
                      variant="outlined"
                      :items="infoItems"
                      :loading="isLoading"
                      :country="leadCountry" />
                  </template>
                </expansion-panel>

                <expansion-panel
                  key="expansion-panel-comments"
                  :title="$t('leads.detail.follow-up.title')"
                  class="expansion-panel-padding"
                  :hide-content-margins="true">
                  <template v-slot:panel-content class="p-0">
                    <banner v-if="isNewStatus" type="warning" :text="$t('leads.detail.follow-up.warning')" />

                    <m-todo-list
                      :buttonText="$t('leads.detail.follow-up.add')"
                      :errorText="$t('leads.detail.follow-up.error')"
                      :placeholder="$t('leads.detail.follow-up.placeholder')"
                      :title="$t('leads.detail.follow-up.title')"
                      :dateText="$t('leads.detail.follow-up.due-date')"
                      v-model="commentLog">
                      <template #task="{task}">
                        <template v-if="task.appointmentId">
                          <div class="appointment-item">
                            <div class="appointment-item__header">
                              <span class="appointment-item__title">{{ task.text }}</span>
                              <m-btn
                                variant="outlined"
                                hoverTextColor="gray"
                                x-small
                                class="appointment-item__btn"
                                @click="goToAppointment(task.appointmentId)">
                                <v-icon x-small class="mr-1 appointment-item__icon"
                                  >fa-solid fa-arrow-up-right-from-square</v-icon
                                >
                                {{ $t('leads.detail.sales-appointments.go-to-appointment') }}
                              </m-btn>
                            </div>
                            <div v-if="task.appointmentComment" class="appointment-item__comment">
                              {{ task.appointmentComment }}
                            </div>
                            <m-chip
                              :text="getAppointmentStatusLabel(task.appointmentStatus)"
                              :variant="getAppointmentStatusVariant(task.appointmentStatus)"
                              :icon="getAppointmentStatusIcon(task.appointmentStatus)"
                              small
                              class="appointment-item__chip" />
                          </div>
                        </template>
                        <template v-else-if="formattedOptionsFollowUpText && task.isRemovable === false">
                          <span v-html="formattedOptionsFollowUpText"></span>
                        </template>
                        <template v-else>
                          <span>{{ task.text }}</span>
                        </template>
                      </template>
                    </m-todo-list>
                  </template>
                </expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-card>

        <v-card-actions class="footer-spacing">
          <m-btn color="primary" class="no-shadow button-padding ml-auto" @click="goBack">
            <span class="button-exit-padding text-uppercase">{{ $t('leads.detail.exit') }}</span>
          </m-btn>
        </v-card-actions>
      </div>

      <mapit-dialog :dialog="dialog" @close="closeConfirmDialog" customId="popup-confirm-lost-lead">
        <template v-slot:header-icon>
          <v-icon class="text-orange">fa-solid fa-hexagon-exclamation</v-icon>
        </template>

        <template v-slot:header-text>
          <div class="text-center">{{ $t('leads.list.confirm-lost-popup.title') }}</div>
        </template>

        <template v-slot:body>
          <div class="text-center">
            <div v-html="$t('leads.list.confirm-lost-popup.body')" />
          </div>
        </template>

        <template v-slot:footer-center>
          <v-btn data-cy="btn-cancel-lost-lead" class="btn-gray" @click="closeConfirmDialog">
            {{ $t('buttons.cancel') }}
          </v-btn>

          <v-btn data-cy="btn-confirm-lost-lead" class="btn-primary" @click="confirmLostLead" :loading="isLoading">
            {{ $t('buttons.confirm') }}
          </v-btn>
        </template>
      </mapit-dialog>
    </card-wrapper>

    <appointment-form-modal
      v-if="showAppointmentForm"
      :is-open="showAppointmentForm"
      :prefilled-contact="prefilledContactData"
      :disable-contact-fields="true"
      :dealer-contact-id="leadDealerContactId"
      :preselected-lead-id="id"
      :on-submit="handleAppointmentSubmit"
      @close="closeAppointmentForm" />
  </div>
</template>

<script lang="ts">
import {Country, CurrencyOptions} from '@mapit/common-utils/dist/types'
import {Status} from '@muvio/honda-offers-builder/model'
import * as Sentry from '@sentry/vue'
import {computed, defineComponent, onMounted, ref, watch} from '@vue/composition-api'
import {parsePhoneNumber} from 'libphonenumber-js'
import moment from 'moment'

import ridoApi from '@/api/rido.api'
import AppointmentFormModal from '@/components/AppointmentFormModal.vue'
import Banner from '@/components/atom/Banner.vue'
import MChip from '@/components/atom/MChip/index.vue'
import {Variant} from '@/components/atom/MChip/types'
import MapitDialog from '@/components/dialog/MapitDialog.vue'
import CardWrapper from '@/components/general/CardWrapper.vue'
import ExpansionPanel from '@/components/general/ExpansionPanel.vue'
import LeadAssigneeSelect from '@/components/leads/LeadAssigneeSelect.vue'
import LeadDetailCard from '@/components/leads/LeadDetailCard.vue'
import {useLead, useOffer} from '@/composables'
import {DealerUser, DealerUserRole, LeadProduct, LeadStatus, OpportunityType} from '@/types'
import {SalesAppointmentStatus} from '@/types/sales-appointments'
import {findSecondLevelRegionById} from '@/utils/administrative-region-helper'
import {parseAppointmentDateTime} from '@/utils/date-parser'
import {mapGetters} from '@/utils/map-store'
import type {CreateAppointmentFormData} from '@/utils/microfrontend-loader'
import {buildFollowUpMessageText} from '@/utils/utils'

export default defineComponent({
  name: 'LeadDetail',
  components: {
    Banner,
    CardWrapper,
    LeadDetailCard,
    ExpansionPanel,
    MapitDialog,
    LeadAssigneeSelect,
    AppointmentFormModal,
    MChip,
  },
  setup(props, {root}) {
    const showAppointmentForm = ref(false)
    const {id} = root.$route.params
    const {
      getCountry,
      getGroup: group,
      isDealerAdmin,
      isSalesAgent,
      getCognitoUser,
      getDealerId,
      getPermissions,
    } = mapGetters(root.$store.getters)

    const currencyOptions = CurrencyOptions[getCountry.value as Country]

    const panels = ref([0, 1])
    const dialog = ref<boolean>(false)
    const dealerUsers = ref<Array<{label: string; value: string}>>([])
    const isDealerUsersLoading = ref(false)

    const {
      commentLog,
      data: lead,
      isCommentLogLoading,
      isLoading,
      lastUpdatedByMe,
      markAsRead,
      maskLeadAsLost,
      refetch: refetchLead,
    } = useLead({id})

    const userEmail = computed(() => {
      const user = getCognitoUser.value
      return user?.attributes?.email || ''
    })

    const formattedOptionsFollowUpText = computed(() => {
      const isOptionsLead = lead.value?.opportunity?.type === OpportunityType.Options
      const hasInitialCommentOnly = commentLog.value?.length === 1

      if (!isOptionsLead || !lead.value?.opportunity?.lease) {
        return null
      }

      return buildFollowUpMessageText(
        root.$i18n.locale,
        {
          appointmentDatetime: lead.value.opportunity.lease.appointmentDatetime,
          estimatedAppointment: lead.value.opportunity.lease.estimatedAppointment,
          regularCustomer: lead.value.opportunity.lease.regularCustomer,
        },
        {
          messageFormat: 'html',
          pendingAppointmentClass: hasInitialCommentOnly ? 'third-party-pending-appointment' : undefined,
          messageClass: 'third-party-follow-up-message',
        },
      )
    })

    const offerId = computed(() => {
      return lead.value?.offerId || undefined
    })

    const dealerId = computed(() => lead.value?.dealer.id)

    const {data: offer, offerPdf, refetch: refetchOffer, isLoading: isOfferLoading} = useOffer({id: offerId, dealerId})

    const clientData = computed(() => {
      return lead.value?.prospect
    })

    const isNewStatus = computed(() => {
      return lead.value?.status === LeadStatus.New
    })

    const isLost = computed(() => lead.value?.status === LeadStatus.Lost)
    const canCreateAppointment = computed(() => {
      return getPermissions.value?.salesAppointments?.canCreate ?? false
    })

    const prefilledContactData = computed(() => {
      if (!lead.value?.prospect) return undefined

      const prospect = lead.value.prospect
      const phone = prospect.phone || ''

      // Use libphonenumber-js to parse the phone number correctly
      // This handles international formats properly and extracts country code
      let countryISO2 = 'ES' // default
      let phoneNumber = phone
      let callingCode = '+34' // default

      try {
        const parsedPhone = parsePhoneNumber(phone)
        if (parsedPhone) {
          countryISO2 = parsedPhone.country || 'ES'
          // Get national number (without country code)
          phoneNumber = parsedPhone.nationalNumber
          // Get country calling code
          callingCode = `+${parsedPhone.countryCallingCode}`
        }
      } catch (error) {
        console.warn('[LeadDetail] Failed to parse phone number:', phone, error)
        // Fallback: keep defaults
      }

      return {
        name: prospect.firstName || '',
        lastName: prospect.lastName || '',
        email: prospect.email || '',
        phone: phoneNumber,
        countryCode: callingCode,
        countryISO2: countryISO2,
        isPhoneValid: true, // Phone from Lead is already valid
      }
    })

    const leadCountry = computed(
      () => (lead.value?.prospect.address?.country as Country) || (getCountry.value as Country),
    )

    const leadDealerContactId = computed(() => lead.value?.dealerContactId)

    const offerTitle = computed(() => {
      return offer.value?.status === Status.Completed || offer.value?.status === Status.Accepted
        ? offer.value?.code?.split('-')[1]
        : root.$t('leads.detail.offer-title')
    })

    const offerFileName = computed(() => {
      return offer.value?.status === Status.Completed || offer.value?.status === Status.Accepted
        ? `${offer.value?.code}.pdf`
        : `${group.value.code}-${offer.value?.id}.pdf`
    })

    const clientItems = computed(() => {
      return [
        {
          id: 'name',
          type: 'text',
          text: root.$t('leads.detail.client.name'),
          value:
            clientData.value?.firstName && clientData.value?.lastName
              ? `${clientData.value?.firstName} ${clientData.value?.lastName}`
              : undefined,
        },
        {
          id: 'email',
          type: 'text',
          text: root.$t('leads.detail.client.email'),
          value: clientData.value?.email || undefined,
        },
        {
          id: 'phone',
          type: 'phone',
          text: root.$t('leads.detail.client.phone'),
          value: clientData.value?.phone || undefined,
        },
        {
          id: 'address',
          type: 'text',
          text: root.$t('leads.detail.client.address'),
          value:
            clientData.value?.address?.streetAddress && clientData.value?.address?.addressNumber
              ? `${clientData.value?.address?.streetAddress}, ${clientData.value?.address?.addressNumber}`
              : undefined,
        },
        {
          id: 'postal-code',
          type: 'text',
          text: root.$t('leads.detail.client.postal-code'),
          value: clientData.value?.address?.postalCode || undefined,
        },
        {
          id: 'city',
          type: 'text',
          text: root.$t('leads.detail.client.city'),
          value: clientData.value?.address?.addressLocality || undefined,
        },
        {
          id: 'region',
          type: 'text',
          text: root.$t('leads.detail.client.region'),
          value: findSecondLevelRegionById(
            clientData.value?.address?.addressRegion as string,
            clientData.value?.address?.country as Country,
          )?.name,
        },
        {
          id: 'country',
          type: 'text',
          text: root.$t('leads.detail.client.country'),
          value: clientData.value?.address?.country
            ? root.$t(`countries.${clientData.value?.address?.country as string}`).toString()
            : undefined,
        },
        {
          id: 'license',
          type: 'text',
          text: root.$t('leads.detail.client.license'),
          value: undefined,
        },
        {
          id: 'document',
          type: 'text',
          text: root.$t('leads.detail.client.document'),
          value: clientData.value?.document?.number || undefined,
        },
      ]
    })

    const leadItems = computed(() => {
      return [
        {
          id: 'createdAt',
          type: 'date',
          text: root.$t('leads.detail.lead.createdAt'),
          value: lead.value?.createdAt,
        },
        {
          id: 'type',
          type: 'text',
          text: root.$t('leads.detail.lead.type'),
          value: lead.value?.opportunity.type ? getItemType(lead.value?.opportunity.type) : undefined,
        },
        {
          id: 'assignee',
          type: 'assignee',
          text: root.$t('leads.detail.lead.assignee'),
          value:
            lead.value?.assignee?.agent ||
            lead.value?.assignee?.agentEmail ||
            root.$t('leads.list.unassigned').toString(),
          loading: isDealerUsersLoading.value || (isCommentLogLoading.value && !lastUpdatedByMe.value),
          assigneeData: {
            agent: lead.value?.assignee,
            initialAgent: lead.value?.initialAgent,
            dealerUsers: dealerUsers.value,
            isDealerAdmin: isDealerAdmin.value,
            isSalesAgent: isSalesAgent.value,
            userEmail: userEmail.value,
            status: lead.value?.status,
            onAssigneeChange,
          },
        },
        {
          id: 'initialAgent',
          type: 'text',
          text: root.$t('leads.detail.lead.initial-agent'),
          value: lead.value?.initialAgent?.agent || lead.value?.initialAgent?.agentEmail,
          // Only show if there's an initial agent AND it's different from current agent
          hidden:
            !lead.value?.initialAgent?.agentEmail ||
            lead.value?.initialAgent?.agentEmail === lead.value?.assignee?.agentEmail,
        },
        {
          id: 'status',
          type: 'text',
          text: root.$t('leads.detail.lead.status'),
          value: `<span class="text-item status status-${lead.value?.status?.toLowerCase()}">${root.$t(
            `leads.status.${lead.value?.status}`,
          )}</span>`,
          loading: isCommentLogLoading.value && isNewStatus.value,
        },
        {
          id: 'offerId',
          type: 'link',
          text: root.$t('leads.detail.lead.offerId'),
          value: lead.value?.offerId
            ? {
                text: offerTitle.value,
                url: offerPdf?.value,
                fileName: offerFileName.value,
              }
            : undefined,
          loading: isOfferLoading.value,
        },
      ]
    })

    const optionItems = computed(() => {
      if (lead.value?.opportunity?.type !== OpportunityType.Options) {
        return []
      }

      const motorbikeSatisfaction = lead.value?.opportunity?.lease?.motorbikeSatisfaction
      const dealerSatisfaction = lead.value?.opportunity?.lease?.dealerSatisfaction

      const options = [
        {
          id: 'operationNumber',
          type: 'text',
          text: root.$t('leads.detail.info.operation-number'),
          value: lead.value?.opportunity.lease?.operationNumber || undefined,
        },
        {
          id: 'salesAgent',
          type: 'text',
          text: root.$t('leads.detail.info.sales-agent'),
          value: lead.value?.opportunity.lease?.salesAgent || undefined,
        },
        {
          id: 'model',
          type: 'text',
          text: root.$t('leads.detail.info.model'),
          value: (lead.value?.opportunity?.product as LeadProduct)?.model || undefined,
          separator: true,
        },
        {
          id: 'priceOfGoodsAmount',
          type: 'text',
          text: root.$t('leads.detail.info.recommended-retail-price'),
          value: lead.value?.opportunity?.lease?.priceOfGoodsAmount || undefined,
        },
        {
          id: 'depositAmount',
          type: 'text',
          text: root.$t('leads.detail.info.deposit-amount'),
          value: lead.value?.opportunity?.lease?.depositAmount || undefined,
        },
        {
          id: 'financedAmount',
          type: 'text',
          text: root.$t('leads.detail.info.financed-amount'),
          value: lead.value?.opportunity?.lease?.financedAmount || undefined,
        },
        {
          id: 'tin',
          type: 'text',
          text: root.$t('leads.detail.info.nominal-interest-rate'),
          value: lead.value?.opportunity?.lease?.tin || undefined,
        },
        {
          id: 'vfg',
          type: 'text',
          text: root.$t('leads.detail.info.guaranteed-future-value'),
          value: lead.value?.opportunity?.lease?.guaranteedFutureValue || undefined,
        },
        {
          id: 'monthlyInstallment',
          type: 'text',
          text: root.$t('leads.detail.info.monthly-installment'),
          value: lead.value?.opportunity?.lease?.monthlyInstallment || undefined,
        },
        {
          id: 'contractStartDate',
          type: 'text',
          text: root.$t('leads.detail.info.formalization-date'),
          value: lead.value?.opportunity?.lease?.contractStartDate
            ? moment(lead.value?.opportunity?.lease?.contractStartDate).format('DD/MM/YYYY')
            : undefined,
        },
        {
          id: 'contractEndDate',
          type: 'text',
          text: root.$t('leads.detail.info.expiration-date'),
          value: lead.value?.opportunity?.lease?.contractEndDate
            ? moment(lead.value?.opportunity?.lease?.contractEndDate).format('DD/MM/YYYY')
            : undefined,
          separator: true,
        },
        {
          id: 'km',
          type: 'text',
          text: root.$t('leads.detail.info.mileage'),
          value: lead.value?.opportunity?.lease?.km || undefined,
        },
        {
          id: 'motorbikeSatisfaction',
          type: 'text',
          text: root.$t('leads.detail.info.motorbike-satisfaction'),
          value: motorbikeSatisfaction
            ? root.$t(`leads.detail.follow-up.options.motorbike-satisfaction.${motorbikeSatisfaction}`)
            : undefined,
        },
        {
          id: 'dealerSatisfaction',
          type: 'text',
          text: root.$t('leads.detail.info.dealer-satisfaction'),
          value: dealerSatisfaction
            ? root.$t(`leads.detail.follow-up.options.dealer-satisfaction.${dealerSatisfaction}`)
            : undefined,
        },
        {
          id: 'usesWorkshop',
          type: 'text',
          text: root.$t('leads.detail.info.uses-workshop'),
          value: lead.value?.opportunity?.lease?.usesWorkshop ? root.$t('common.yes') : root.$t('common.no'),
        },
        {
          id: 'comments',
          type: 'text',
          text: root.$t('leads.detail.info.comments'),
          value: lead.value?.opportunity?.lease?.comments || undefined,
        },
      ].filter(item => item.value !== undefined)

      return options
    })

    const regularInfoItems = computed(() => {
      if (lead.value?.opportunity?.type === OpportunityType.Options) {
        return []
      }

      return [
        {
          id: 'model',
          type: 'text',
          text: root.$t('leads.detail.info.model'),
          value: (lead.value?.opportunity?.product as LeadProduct)?.model || undefined,
        },
        {
          id: 'variant',
          type: 'text',
          text: root.$t('leads.detail.info.variant'),
          value: (lead.value?.opportunity?.product as LeadProduct)?.variant || undefined,
        },
        {
          id: 'year',
          type: 'text',
          text: root.$t('leads.detail.info.year'),
          value: getModelYear(lead.value?.opportunity?.product as LeadProduct),
        },
        {
          id: 'color',
          type: 'text',
          text: root.$t('leads.detail.info.color'),
          value: (lead.value?.opportunity?.product as LeadProduct)?.color || undefined,
        },
        {
          id: 'reservation',
          type: 'text',
          text: root.$t('leads.detail.info.reservation'),
          value: lead.value?.opportunity?.reservation?.number || undefined,
        },
        {
          id: 'reservationStatus',
          type: 'text',
          text: root.$t('leads.detail.info.reservation-status'),
          value: lead.value?.opportunity?.reservation?.status || undefined,
        },
        {
          id: 'amount',
          type: 'text',
          text: root.$t('leads.detail.info.amount'),
          value: lead.value?.opportunity?.expectedRevenue
            ? Intl.NumberFormat(currencyOptions.locale, {
                style: 'currency',
                currency: currencyOptions.currency,
              }).format(lead.value?.opportunity?.expectedRevenue as number)
            : undefined,
        },
        {
          id: 'channel.supplier',
          type: 'text',
          text: root.$t('leads.detail.info.supplier'),
          value: lead.value?.channel?.supplier || undefined,
        },
        {
          id: 'testRide.requestedDate',
          type: 'text',
          text: root.$t('leads.detail.info.test-ride.requested-date'),
          value: lead.value?.opportunity?.testRide?.requestedDate || undefined,
        },
        {
          id: 'testRide.timeslot',
          type: 'text',
          text: root.$t('leads.detail.info.test-ride.timeslot'),
          value: lead.value?.opportunity?.testRide?.timeslot || undefined,
        },
        {
          id: 'comments',
          type: 'text',
          text: root.$t('leads.detail.info.comments'),
          value: lead.value?.opportunity?.comments || undefined,
        },
      ]
    })

    const infoItems = computed(() => {
      return lead.value?.opportunity.type === OpportunityType.Options ? optionItems.value : regularInfoItems.value
    })

    onMounted(async () => {
      // Only fetch dealer users if user is dealer-admin
      // Sales-agents and other roles don't need the full dealer users list
      if (isDealerAdmin.value && getDealerId.value) {
        isDealerUsersLoading.value = true
        try {
          const response = await ridoApi.listDealerUsers(getDealerId.value)
          if (response.data && response.data.data) {
            // Filter to only include dealer-admin and sales-agent roles
            dealerUsers.value = response.data.data
              .filter(
                (user: DealerUser) =>
                  user.role === DealerUserRole.DealerAdmin || user.role === DealerUserRole.SalesAgent,
              )
              .map((user: DealerUser) => ({
                label: user.name,
                value: user.email,
              }))
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: {component: 'LeadDetail', action: 'fetchDealerUsers'},
          })
        } finally {
          isDealerUsersLoading.value = false
        }
      }
    })

    watch(isLoading, async () => {
      if (!isLoading.value) {
        if (!lead.value?.read) await markAsRead()
        await refetchOffer()
      }
    })

    function getModelYear(product: LeadProduct | undefined) {
      let res = undefined
      try {
        if (product?.variant) {
          const match = product?.variant.match(/.+(\d{4})/)
          res = match && match.length > 1 ? match[1] : undefined
        }
      } catch (e) {
        console.error(e)
      }
      return res
    }

    function goBack() {
      root.$router.go(-1)
    }

    function createOffer() {
      root.$router.push({name: 'CreateOfferMotorbike', params: {leadId: lead.value?.id || ''}})
    }

    function seeOffer() {
      root.$router.push({
        name: 'ResumeOfferMotorbike',
        params: {
          id: lead.value?.offerId || '',
          leadId: lead.value?.id || '',
        },
      })
    }

    function closeConfirmDialog() {
      dialog.value = false
    }

    function openConfirmDialog() {
      dialog.value = true
    }

    function confirmLostLead() {
      maskLeadAsLost()
      closeConfirmDialog()
    }

    function openAppointmentForm() {
      showAppointmentForm.value = true
    }

    function closeAppointmentForm() {
      showAppointmentForm.value = false
    }

    async function handleAppointmentSubmit(formData: CreateAppointmentFormData) {
      try {
        const {startDate, endDate} = parseAppointmentDateTime(
          formData.date,
          formData.startTime,
          formData.endTime,
          root.$i18n.locale,
        )

        const commonPayload = {
          appointmentType: formData.appointmentType,
          startDate: startDate.toUTC().toISO()!,
          endDate: endDate.toUTC().toISO()!,
          comment: formData.comment || undefined,
          product: formData.model ? {model: formData.model} : undefined,
        }

        if (formData.leadSelection.type === 'existing') {
          await ridoApi.createSalesAppointment({
            ...commonPayload,
            leadId: formData.leadSelection.leadId,
          })
        } else {
          await ridoApi.createSalesAppointmentAndLead({
            ...commonPayload,
            contact: {
              name: formData.contact.name,
              phone: formData.contact.phone,
              email: formData.contact.email || undefined,
            },
          })
        }

        await refetchLead()

        closeAppointmentForm()
        root.$store.dispatch('showAlertSuccess', root.$t('leads.list.actions.appointment-created'))
      } catch (err: any) {
        Sentry.captureException(err, {
          tags: {component: 'LeadDetail', action: 'createAppointment'},
        })
        // Propagate error to React to show toast in modal
        throw err
      }
    }

    async function onAssigneeChange(newAssignee: string) {
      const currentAssigneeEmail = lead.value?.assignee?.agentEmail || ''
      if (currentAssigneeEmail === newAssignee) return

      try {
        await ridoApi.assignLead(id, newAssignee)
        root.$store.dispatch('showAlertSuccess', 'leads.list.agent-assigned-success')

        // Notify the sales-appointments MF so it can optimistically update the cached agent
        const assigneeName = dealerUsers.value.find(u => u.value === newAssignee)?.label
        window.dispatchEvent(
          new CustomEvent('mf:lead-assigned', {
            detail: {leadId: id, assigneeEmail: newAssignee, assigneeName},
          }),
        )

        await refetchLead()
      } catch (error: any) {
        Sentry.captureException(error, {
          tags: {component: 'LeadDetail', action: 'assignLead'},
        })
        const errorMessage = error?.response?.data?.error || error?.response?.data?.message
        if (errorMessage?.includes('inactive user')) {
          root.$store.dispatch('showAlertError', 'leads.list.inactive-user-error')
        } else {
          root.$store.dispatch('showAlertError', 'common.error')
        }
        // Refetch lead to ensure UI shows correct state after error
        await refetchLead()
      }
    }

    function getItemType(key: string) {
      return root.$te(`leads.types.${key}`) ? root.$t(`leads.types.${key}`) : key
    }

    function goToAppointment(appointmentId: string) {
      root.$router.push({name: 'SalesAppointmentDetail', params: {id: appointmentId}})
    }

    function getAppointmentStatusLabel(status: string) {
      return root.$t(`leads.detail.sales-appointments.status.${status}`).toString()
    }

    function getAppointmentStatusVariant(status: string): Variant {
      const map: Record<string, Variant> = {
        [SalesAppointmentStatus.Pending]: Variant.Warning,
        [SalesAppointmentStatus.Accepted]: Variant.Info,
        [SalesAppointmentStatus.Rejected]: Variant.Error,
      }
      return map[status] || Variant.Info
    }

    function getAppointmentStatusIcon(status: string): string {
      const map: Record<string, string> = {
        [SalesAppointmentStatus.Pending]: 'fa-hourglass',
        [SalesAppointmentStatus.Accepted]: 'fa-check',
        [SalesAppointmentStatus.Rejected]: 'fa-xmark',
      }
      return map[status] || 'fa-circle-question'
    }

    return {
      id,
      showAppointmentForm,
      openAppointmentForm,
      closeAppointmentForm,
      handleAppointmentSubmit,
      formattedOptionsFollowUpText,
      offer,
      commentLog,
      isLost,
      canCreateAppointment,
      prefilledContactData,
      leadDealerContactId,
      offerId,
      isLoading,
      panels,
      clientData,
      clientItems,
      leadItems,
      infoItems,
      isNewStatus,
      dialog,
      leadCountry,
      goBack,
      createOffer,
      seeOffer,
      confirmLostLead,
      openConfirmDialog,
      closeConfirmDialog,
      dealerUsers,
      isDealerAdmin,
      isSalesAgent,
      userEmail,
      onAssigneeChange,
      goToAppointment,
      getAppointmentStatusLabel,
      getAppointmentStatusVariant,
      getAppointmentStatusIcon,
    }
  },
})
</script>

<style lang="scss">
#leads-detail {
  .appointment-item__chip {
    border: 1px solid white !important;
  }

  .appointment-item__btn.gray--text:not(:hover) {
    color: inherit !important;
  }

  td:has(> .appointment-item) {
    background-color: #f2f2f2;
    border-radius: 5px 0 0 5px;
    vertical-align: top;
  }

  td:has(> .appointment-item) + td {
    background-color: #f2f2f2;
    border-radius: 0 5px 5px 0;
    vertical-align: top;
    color: #000;
  }

  .todolist .date-cell:not(:has(.delete-btn)) > span {
    margin-right: 44px;
  }

  .todolist table {
    table-layout: auto;
  }

  .todolist table th:nth-child(2),
  .todolist table td:nth-child(2) {
    width: 160px;
  }

  hr.v-divider {
    margin-top: 0 !important;
  }

  .v-expansion-panel.expansion-panel-padding {
    padding-right: 0 !important;
    padding-left: 36px !important;
    padding-bottom: 32px !important;
  }

  @media (max-width: 1264px) {
    .v-expansion-panel.expansion-panel-padding {
      padding-top: 22px;
      padding-right: 0 !important;
      padding-left: 0 !important;
      padding-bottom: 0 !important;
    }
  }
}

.third-party {
  &-pending-appointment {
    background-color: #fff2d9;
    color: #fba401;
    padding: 0.15rem;
    border-radius: 0.25rem;
  }

  &-follow-up-message {
    font-weight: bold;
    color: #9f9f9f;
  }
}
</style>
<style scoped lang="scss">
.min-height {
  height: min-content !important;
}

.card-title-padding {
  padding: 7px 0 2px 0;
}

.pl-lead-grid {
  padding-left: 36px;
}

.lead-grid {
  display: grid;
  grid-template-columns: 1fr;
  margin: 0;
  gap: 22px;
  padding: 0;
}

.card-spacing {
  padding: 0 60px 40px 60px;
}

.card-outline {
  outline: 1px solid #dbdbdb;
}

.footer-spacing {
  padding: 30px 0 0 0;
}

.button-exit-padding {
  padding: 10px 40px;
  font-family: 'ProximaNovaExCnSbold', sans-serif;
  font-size: 16px;
}

.button-padding {
  padding: 0 20px;
  display: flex;
  align-items: center;
}

.btn-link-mapit:hover {
  .fa-m-file-arrow-right {
    color: white !important;
  }
}

.no-shadow {
  box-shadow: none;
}

.no-margin {
  margin: 0;
}

.lead-layout-spacing {
  padding: 27px 40px 40px 40px;
}

.title-row {
  display: flex;
  align-items: center;
  padding: 27px 40px 0 40px;

  h1 {
    font-size: 32px;
    font-weight: 500;
    font-family: 'ProximaNovaExCnSBold', serif;
    margin: 0;
  }

  .button-wrapper {
    margin-left: auto;

    button {
      margin-left: 16px;
    }
  }
}

.leads-detail-wrapper {
  padding: 40px;
  font-size: 15px;
}

.no-padding {
  padding: 0 !important;
}

@media (max-width: 1264px) {
  .card-spacing {
    padding: 0 0 40px 0;
  }

  .lead-layout-spacing {
    padding: 27px 30px 30px 30px;
  }

  .footer-spacing {
    padding: 30px 0 0 0;
  }

  .pl-lead-grid {
    padding-left: 0px;
  }

  #leads-detail .v-expansion-panel.expansion-panel-padding {
    padding-right: 0 !important;
    padding-left: 0px !important;
  }
}

@media (max-width: 1264px) {
  #lead-grid-left-column {
    grid-template-columns: 1fr 1fr;
  }
}

.todolist {
  white-space: pre-wrap;
}

.appointment-item {
  &__header {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: nowrap;
    margin-bottom: 4px;
  }

  &__title {
    font-family: 'ProximaNovaSemibold', sans-serif;
    font-size: 14px;
    min-width: 0;
  }

  &__chip {
    margin-top: 4px;
  }

  &__comment {
    font-size: 13px;
    color: #666;
    margin-bottom: 4px;
  }

  &__type {
    font-size: 13px;
    color: #666;
    margin-bottom: 4px;
  }

  &__btn.muvue-btn {
    height: 30px !important;
    padding-left: 0.35rem !important;
    padding-right: 0.25rem !important;
    border-color: #9a9a9a !important;
    margin-left: auto;
    flex-shrink: 0;
  }

  &__icon {
    transition: none !important;
  }
}
</style>
